import { Send, Shield, Globe, Zap, Clock, CreditCard, Star, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Home() {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', message: '' });
        }, 3000);
    };

    const testimonials = [
        {
            name: 'Sarah Johnson',
            quote: 'Mojo Money has made sending money home so easy! The rates are competitive and the service is incredibly fast.',
            rating: 5,
            photo: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=200'
        },
        {
            name: 'Michael Chen',
            quote: 'I trust Mojo Money for all my remittance needs. Their security features give me peace of mind.',
            rating: 5,
            photo: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=200'
        },
        {
            name: 'Fatima Hassan',
            quote: 'The best money transfer service I have used. Quick, reliable, and excellent customer support!',
            rating: 5,
            photo: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=200'
        }
    ];

    return (
        <div className="min-h-screen">
            <section className="relative bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 text-white pt-32 pb-20">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">
                            Send Money Home with Confidence
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 text-emerald-50">
                            Fast, secure, and reliable money transfers to Ethiopia. Join thousands of satisfied customers.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/exchange-rates" className="bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105">
                                Start Sending Money
                            </Link>
                            <Link to="/exchange-rates" className="bg-white text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-50 transition transform hover:scale-105">
                                Make Payments
                            </Link>
                            <Link to="/merchants" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-emerald-900 transition transform hover:scale-105">
                                Be a Merchant
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-white border-b">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
                        <div className="flex items-center space-x-3">
                            <Shield className="w-8 h-8 text-emerald-800" />
                            <span className="text-gray-700 font-semibold">Bank-Level Security</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <CheckCircle className="w-8 h-8 text-emerald-800" />
                            <span className="text-gray-700 font-semibold">Licensed & Regulated</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Globe className="w-8 h-8 text-emerald-800" />
                            <span className="text-gray-700 font-semibold">Multi-Currency Support</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center text-emerald-900 mb-4">
                        Why Choose Mojo Money?
                    </h2>
                    <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                        Experience the best in money transfer services with features designed for your convenience and security.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105">
                            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                                <Zap className="w-8 h-8 text-emerald-800" />
                            </div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-3">Instant Transfers</h3>
                            <p className="text-gray-600">
                                Send money in seconds with real-time processing and immediate notifications.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105">
                            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                                <Shield className="w-8 h-8 text-emerald-800" />
                            </div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-3">Secure Payments</h3>
                            <p className="text-gray-600">
                                Bank-level encryption and fraud detection to protect every transaction.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105">
                            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                                <Globe className="w-8 h-8 text-emerald-800" />
                            </div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-3">Multi-Currency</h3>
                            <p className="text-gray-600">
                                Support for multiple currencies with competitive exchange rates updated daily.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition transform hover:scale-105">
                            <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                                <Clock className="w-8 h-8 text-emerald-800" />
                            </div>
                            <h3 className="text-xl font-bold text-emerald-900 mb-3">24/7 Support</h3>
                            <p className="text-gray-600">
                                Round-the-clock customer support ready to assist you anytime, anywhere.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center text-emerald-900 mb-4">
                        What Our Users Say
                    </h2>
                    <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
                        Join thousands of satisfied customers who trust Mojo Money for their money transfer needs.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-gray-50 p-8 rounded-xl shadow-lg">
                                <div className="flex items-center mb-4">
                                    <img
                                        src={testimonial.photo}
                                        alt={testimonial.name}
                                        className="w-16 h-16 rounded-full object-cover mr-4"
                                    />
                                    <div>
                                        <h4 className="font-bold text-emerald-900">{testimonial.name}</h4>
                                        <div className="flex">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 bg-gradient-to-br from-emerald-800 to-emerald-900 text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-4xl font-bold text-center mb-4">
                            Ready to Get Started?
                        </h2>
                        <p className="text-center text-emerald-50 mb-12 text-lg">
                            Contact us today and experience the best money transfer service for the Ethiopian diaspora.
                        </p>

                        {submitted ? (
                            <div className="bg-white p-8 rounded-xl text-center">
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                <h3 className="text-2xl font-bold text-emerald-900 mb-2">Thank You!</h3>
                                <p className="text-gray-600">We'll get back to you shortly.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-xl">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <label className="block text-emerald-900 font-semibold mb-2">Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                            placeholder="Your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-emerald-900 font-semibold mb-2">Email</label>
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>
                                </div>
                                <div className="mb-6">
                                    <label className="block text-emerald-900 font-semibold mb-2">Message</label>
                                    <textarea
                                        required
                                        value={formData.message}
                                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        rows={4}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-900"
                                        placeholder="How can we help you?"
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105"
                                >
                                    Send Message
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
